import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  SettingsDrawerButton,
  SettingsDrawerLink,
} from '../../../settings/settings-drawer.style';
import IconLeave from 'approot/shared/icons/icon-leave';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { PresentDrawerItem } from 'approot/activity-builder/present/activity-builder-present.constants';
import { ACTIVITY_BUILDER_SETTINGS } from 'approot/activity-builder/present/activity-builder-present-skip-links';
import { BoxList } from 'approot/shared/design-system/box/box.style';
import * as Styled from 'approot/activity-builder/present/menu/present-menu.style';
import {
  EVENT_ACTION_EXIT,
  EVENT_ACTION_OPEN,
  EVENT_TARGET_TYPE_ACTIVITIES,
  EVENT_TARGET_TYPE_MENU,
} from 'approot/shared/data-tracker/data-events.constants';
import { pushActivityBuilderPresentMenuEvent } from 'approot/activity-builder/present/activity-builder-present-data-tracking.utils';
import { StudentSectionOrSubSectionOverview } from 'approot/activity-builder/student/student.constants';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { ACTIVITIES_CONTENT_STATUS_PUBLISHED } from 'global/constants';
import IconActivitiesActive from 'approot/shared/icons/icon-activities-active';
import IconActivities from 'approot/shared/icons/icon-activities';
import IconDownload from 'approot/shared/icons/icon-download';
import IconTranslateActive from 'approot/shared/icons/icon-translate-active';
import IconTranslate from 'approot/shared/icons/icon-translate';
import { getClassActivitiesExitUrl } from './get-class-activities-exit-url';

type Props = {
  isLoggedIn: boolean;
  activeSectionId: string;
  currentSectionOverview: StudentSectionOrSubSectionOverview | null;
  currentSectionOverviewIndex: number;
  sectionsOverview: StudentSectionOrSubSectionOverview[];
  handleOpen: (id: PresentDrawerItem) => void;
  handleDownload: () => void;
};

const StudentPresentMenu = ({
  isLoggedIn,
  activeSectionId,
  currentSectionOverview,
  currentSectionOverviewIndex,
  sectionsOverview,
  handleOpen,
  handleDownload,
}: Props) => {
  const { drawer, drawerType, isZoomToFit } =
    useActivityBuilderPresentContext();

  const { contentType, contentId, version } = useGetInteractiveLessonParams();

  const location = useLocation();
  // Compute the path up to '/activities' (exclusive) and preserve assignmentId if present
  const exitTo = useMemo(
    () => getClassActivitiesExitUrl(location.pathname, location.search),
    [location.pathname, location.search]
  );

  // when menu appears focus the container
  useEffect(() => {
    if (drawer.isActive) {
      document.getElementById(ACTIVITY_BUILDER_SETTINGS)?.focus();
    }
  }, [drawer.isActive]);

  return (
    <Styled.PresentMenuContainer
      id={ACTIVITY_BUILDER_SETTINGS}
      aria-label="Presentation Menu Options"
      tabIndex={-1}
      data-selector="present-menu-options"
    >
      <BoxList role="group" aria-label="Main Menu Actions">
        <li>
          <SettingsDrawerButton
            data-selector="present-menu-activities"
            type="button"
            $isActive={drawerType === PresentDrawerItem.Activities}
            data-testid="activities"
            onClick={() => {
              pushActivityBuilderPresentMenuEvent({
                eventAction: EVENT_ACTION_OPEN,
                eventTargetIdentifier: EVENT_TARGET_TYPE_ACTIVITIES,
                contentId,
                contentType,
                sectionId: activeSectionId,
                currentSectionOverview,
                sectionsOverview,
                version,
                status: ACTIVITIES_CONTENT_STATUS_PUBLISHED,
                isZoomToFit,
                currentSectionOverviewIndex,
              });
              handleOpen(PresentDrawerItem.Activities);
            }}
          >
            {drawerType === PresentDrawerItem.Activities ? (
              <IconActivitiesActive width={24} height={24} rotate={270} />
            ) : (
              <IconActivities width={24} height={24} rotate={270} />
            )}
            <span>Activities</span>
          </SettingsDrawerButton>
        </li>
        {isLoggedIn && (
          <li>
            <SettingsDrawerButton
              data-selector="present-menu-translate"
              type="button"
              data-testid="present-menu-translate"
              $isActive={drawerType === PresentDrawerItem.Translate}
              onClick={() => handleOpen(PresentDrawerItem.Translate)}
            >
              {drawerType === PresentDrawerItem.Translate ? (
                <IconTranslateActive width={20} height={20} />
              ) : (
                <IconTranslate width={20} height={20} />
              )}
              <span>Translate</span>
            </SettingsDrawerButton>
          </li>
        )}
      </BoxList>
      <BoxList role="group" aria-label="Secondary Menu Actions">
        <li>
          <SettingsDrawerButton
            data-selector="present-menu-download"
            type="button"
            $isActive={false}
            onClick={handleDownload}
          >
            <IconDownload width={24} height={24} />
            <span>Download</span>
          </SettingsDrawerButton>
        </li>
        <li>
          <SettingsDrawerLink
            data-selector="present-menu-exit"
            to={exitTo}
            $isActive={false}
            data-testid="exit"
            onClick={() => {
              pushActivityBuilderPresentMenuEvent({
                eventAction: EVENT_ACTION_EXIT,
                eventTargetIdentifier: EVENT_TARGET_TYPE_MENU,
                contentId,
                contentType,
                sectionId: activeSectionId,
                currentSectionOverview,
                sectionsOverview,
                version,
                status: ACTIVITIES_CONTENT_STATUS_PUBLISHED,
                isZoomToFit,
                currentSectionOverviewIndex,
              });
            }}
          >
            <IconLeave width={24} height={24} />
            <span>Exit</span>
          </SettingsDrawerLink>
        </li>
      </BoxList>
    </Styled.PresentMenuContainer>
  );
};

export default StudentPresentMenu;
